/**
 * The `file` directive renderer — the wizard's concept-image upload step. Hands
 * a picked image to the injected `onFile` seam (the media-ingest path re-validates
 * type + size and enforces red line 2 — your own character concept only, no
 * real/identifiable person). When no `onFile` is wired (e.g. before the builder
 * is mounted) the control degrades to its hint rather than faking an upload.
 */
import type { UiDirective } from '@4f/shared';
import { useT } from '@4f/ui';
import { type JSX, Show } from 'solid-js';
import type { GenerativeUiHandlers } from '../GenerativeUi.tsx';

type FileDirective = Extract<UiDirective, { kind: 'file' }>;

export interface FileRendererProps {
  readonly directive: FileDirective;
  readonly handlers: GenerativeUiHandlers;
}

export function FileRenderer(props: FileRendererProps): JSX.Element {
  const { t } = useT();

  const pick =
    (onFile: (file: File) => void) =>
    (event: Event): void => {
      const input = event.currentTarget as HTMLInputElement;
      const file = input.files?.[0];
      if (file) {
        onFile(file);
      }
    };

  return (
    <div class="f4-genui-file">
      <Show when={props.directive.title}>
        {(title) => <h3 class="f4-genui__title">{title()}</h3>}
      </Show>
      <Show when={props.directive.prompt}>
        {(prompt) => <p class="f4-genui__prompt">{prompt()}</p>}
      </Show>
      <Show
        when={props.handlers.onFile}
        fallback={<p class="f4-genui__hint">{t('surface.fileHint')}</p>}
      >
        {(onFile) => (
          <label class="f4-genui-file__label">
            <span>{t('surface.chooseFile')}</span>
            <input
              type="file"
              class="f4-genui-file__input"
              accept={props.directive.accept.join(',')}
              disabled={props.handlers.disabled}
              onChange={pick(onFile())}
            />
            <span class="f4-genui__hint">{t('surface.fileHint')}</span>
          </label>
        )}
      </Show>
    </div>
  );
}
